import React, { useCallback, useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import toast from "react-hot-toast";
import Icon from "../icons";
import { useUsersContext } from "../../context/usersContext";

const MAX_SINGLE_FILE_SIZE = 32 * 1024 * 1024;
const MAX_COMBINED_FILE_SIZE = 50 * 1024 * 1024;

const MyDropzone = ({ gettingfilefromdropzone }) => {
  const { getAttachedFileSize } = useUsersContext();
  const [files, setFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [loader, setLoader] = useState(false);
  const [firstscreen, setFirstscreen] = useState(false);
  const [audioFileChecks, setAudioFileChecks] = useState({});

  const allowedTypes = [
    // Images
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",

    // Text & markup
    "text/plain",
    "text/markdown",
    "text/html",
    "text/css",
    "application/xml",

    // Code & data
    "application/json",
    "application/javascript",
    "application/typescript",
    "application/x-sh",
    "application/x-httpd-php",
    "application/x-tex",

    // Programming languages
    "text/x-python",
    "text/x-java-source",
    "text/x-c",
    "text/x-c++src",
    "text/x-csharp",
    "application/x-ruby",

    // Documents
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.oasis.opendocument.text",
    "application/rtf",

    // Presentations
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",

    // Spreadsheets
    "text/csv",
    "text/tab-separated-values",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",

    // Audio
    "audio/mpeg",
    "audio/mpga",
    "audio/wav",
    "audio/mp4",
    "audio/x-m4a",
    "audio/flac",

    // Video
    "video/mp4",
    "video/mpeg",
    "video/webm"
  ];

  const audiofilestype = [
    "audio/mpeg",   // mp3
    "audio/mpga",   // mpga
    "audio/wav",    // wav
    "audio/mp4",    // m4a
    "audio/x-m4a",  // m4a
    "audio/flac",   // flac

    // if treating video as playable media
    "video/mp4",
    "video/mpeg",
    "video/webm"
  ];

  useEffect(() => {
    const storedFiles = localStorage.getItem("uploadedFiles");
    if (storedFiles) {
      setFiles(JSON.parse(storedFiles));
    }
  }, []);

  const saveFilesToStorage = (updatedFiles) => {
    localStorage.setItem("uploadedFiles", JSON.stringify(updatedFiles));
    setLoader(false);
  };

  const allowedExtensions =
    /\.(txt|py|js|ts|json|md|csv|tsv|pdf|doc|docx|odt|rtf|xls|xlsx|ppt|pptx|mp3|mpga|mp4|mpeg|webm|wav|m4a|flac|html|css|xml|c|cpp|cs|java|php|rb|tex|sh|jpg|jpeg|png|gif|webp)$/i;

  const isValidFile = (file) => {
    return (
      (file.type && allowedTypes[file.type]) ||
      allowedExtensions.test(file.name)
    );
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      const filteredFiles = acceptedFiles.filter(isValidFile);
      if (filteredFiles.length !== acceptedFiles.length) {
        const invalidFiles = acceptedFiles.filter((file) => !isValidFile(file));
        const invalidFileNames = invalidFiles
          .map((file) => file.name)
          .join(", ");
        setErrorMessage(
          `<p style="color:red;">Only specific file types are allowed. Invalid files : <br></br>${invalidFileNames} <p>`,
        );
        return;
      }

      const oversizedFile = filteredFiles.find(
        (file) => file.size > MAX_SINGLE_FILE_SIZE,
      );
      if (oversizedFile) {
        const msg = `${oversizedFile.name}  exceeds the 32 MB file size limit. Please choose a smaller file`;
        toast.error(msg);
        setErrorMessage(`<div>
          <p style="color:red;font-weight:bold;font-size:16px;">Individual file exceeds 32 MB</p>
          <p style="color:red;">${msg}</p>
          </div>`);
        return;
      }


      const alreadyAttached = getAttachedFileSize ? getAttachedFileSize() : 0;
      const existingSize = files.reduce(
        (total, { file }) => total + (file?.size || 0),
        0,
      );
      const incomingSize = filteredFiles.reduce(
        (total, file) => total + file.size,
        0,
      );
      if (
        alreadyAttached + existingSize + incomingSize >
        MAX_COMBINED_FILE_SIZE
      ) {
        const msg = "Your selected files exceed the 50 MB upload limit. Remove some files or upload them in smaller batches.";
        toast.error(msg);
        setErrorMessage(`<div>
          <p style="color:red;font-weight:bold;font-size:16px;">Total upload size exceeds 50 MB</p>
          <p style="color:red;">${msg}</p>
          </div>`);
        return;
      }

      const updatedFiles = filteredFiles.map((file) => ({
        file,
        progress: 0,
        completed: false,
      }));

      setFiles([...files, ...updatedFiles]);
      saveFilesToStorage([...files, ...updatedFiles]);
      if (errorMessage !== "") {
        setErrorMessage("");
        setFirstscreen(true);
      } else {
        setFirstscreen(true);
      }

      const interval = setInterval(() => {
        setFiles((prevFiles) =>
          prevFiles.map((prevFile) => {
            const updatedFile = updatedFiles.find(
              (file) => file.file === prevFile.file,
            );
            if (updatedFile) {
              if (prevFile.progress < 100) {
                setLoader(true);
                setFirstscreen(true);
                updatedFile.progress += 10;
              } else if (prevFile.progress >= 100) {
                updatedFile.progress = 100;
                updatedFile.completed = true;
                clearInterval(interval);
              }
            }
            return updatedFile || prevFile;
          }),
        );
        saveFilesToStorage(files);
      }, 1000);

      return () => clearInterval(interval);
    },
    [files, saveFilesToStorage],
  );

  const handlesave = (e) => {
    e.preventDefault();
    const filesWithCheckboxes = files.map((file) => ({
      ...file,
      isChecked: audioFileChecks[file.file.name] || false,
    }));
    gettingfilefromdropzone(filesWithCheckboxes);
    setLoader(true);
    localStorage.removeItem('uploadedFiles');
  };

  const handleDelete = (fileToDelete) => {
    const updatedFiles = files.filter((file) => file.file !== fileToDelete);
    setFiles(updatedFiles);
    saveFilesToStorage(updatedFiles);
    if (updatedFiles.length < 1) {
      setFirstscreen(false);
    }
  };

  const handleCheckboxChange = (fileName, isChecked) => {
    setAudioFileChecks({
      ...audioFileChecks,
      [fileName]: isChecked,
    });
  };

  const restart = () => {
    setFiles([]);
    saveFilesToStorage([]);
    setFirstscreen(false);
  };

  return (
    <div className="maindiv">
      {errorMessage && (
        <div
          className="error-message text-center"
          style={{ marginBottom: "10px" }}
        >
          <div dangerouslySetInnerHTML={{ __html: errorMessage }}></div>
        </div>
      )}
      <Dropzone onDrop={onDrop} accept={allowedTypes}>
        {({ getRootProps, getInputProps }) => (
          <>
            {firstscreen === false && (
              <div {...getRootProps()} className="dropzone">
                <input {...getInputProps()} />
                {files.length <= 0 && (
                  <div className="text-center" id="removetext">
                    <p>Drag & drop files here, or click to select files</p>
                    <button className="uploadbutton">Upload Files</button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Dropzone>
      <div
        className="text-right position-absolute"
        style={{ top: 0, right: "72px" }}
      >
        {files.length > 0 && (
          <>
            <button onClick={restart} className="px-2 mx-2 cancelbutton">
              Cancel
            </button>
            {loader ? (
              <button className="uploadbutton">wait...</button>
            ) : (
              <button onClick={handlesave} className="uploadbutton">
                Continue
              </button>
            )}
          </>
        )}
      </div>
      <div className="file-list">
        {files.map((uploadedFile, index) => (
          <div key={index} className="file-item">
            <p className="ellipsetext">{uploadedFile.file.name}</p>
            <div className="deleterow">
              <p style={{ margin: "10px 0px" }}>
                {parseFloat(uploadedFile.file.size / (1024 * 1024)).toFixed(2)}{" "}
                MB
              </p>
              <p
                className="px-2"
                onClick={() => handleDelete(uploadedFile.file)}
              >
                {" "}
                {uploadedFile.completed && <Icon id="delete" />}
              </p>
            </div>
            {audiofilestype.includes(uploadedFile.file.type) && (
              <div>
                <label>
                  <input
                    type="checkbox"
                    checked={audioFileChecks[uploadedFile.file.name] || false}
                    onChange={(e) =>
                      handleCheckboxChange(
                        uploadedFile.file.name,
                        e.target.checked,
                      )
                    }
                  />
                  Translate
                </label>
              </div>
            )}
            {!uploadedFile.completed && (
              <progress
                className="progress-z"
                value={uploadedFile.progress}
                max="100"
              />
            )}
            {uploadedFile.completed && <p className="success-message"></p>}
            {!uploadedFile.completed && (
              <button
                onClick={() => handleDelete(uploadedFile.file)}
                className="crossicon-z delete-button"
              >
                X
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyDropzone;
